import { getPlatform } from '../constants/platforms';

const CURRENCY_SYMBOLS = {
  INR: '₹',
  USD: '$',
  EUR: '€',
  GBP: '£',
  AED: 'AED ',
  SAR: 'SAR ',
};

export function getStorePlatform(profile) {
  return profile?.platform || 'amazon';
}

export function getStoreId(profile) {
  return String(profile?.profileId || profile?.storeId || profile?.id || '');
}

export function getStoreName(profile) {
  return (
    profile?.storeName ||
    profile?.name ||
    profile?.displayName ||
    `Store ${getStoreId(profile)}`
  );
}

export function getStoreRegion(profile) {
  return profile?.countryCode || profile?.marketplace || profile?.region || '—';
}

export function getStoreCurrency(profile) {
  return profile?.currencyCode || profile?.currency || '—';
}

export function formatStoreCurrency(amount, currencyCode) {
  if (amount == null || amount === '' || Number(amount) >= 999999999) return null;
  const code = currencyCode || 'INR';
  const symbol = CURRENCY_SYMBOLS[code] || `${code} `;
  const num = Number(amount);
  if (!Number.isFinite(num)) return null;
  return `${symbol}${num.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export function resolveConnectionStatus(profile) {
  if (profile?.connectionStatus) return profile.connectionStatus;
  if (profile?.granted === false) return 'disconnected_seller';
  if (profile?.isConnected === false) return 'disconnected_seller';
  return 'connected';
}

export function connectionStatusLabel(status) {
  switch (status) {
    case 'connected':
      return 'Connected';
    case 'disconnected_admin':
      return 'Disconnected by admin';
    case 'disconnected_seller':
      return 'Disconnected by seller';
    case 'token_missing':
      return 'Token missing';
    case 'token_error':
      return 'Connection error';
    default:
      return 'Unknown';
  }
}

export function isStoreConnected(profile) {
  const status = resolveConnectionStatus(profile);
  return status === 'connected';
}

export function isStoreViewable(profile) {
  return Boolean(profile?.profileId || profile?.storeId);
}

export function normalizeStoreProfile(profile) {
  if (!profile) return null;
  const platform = getStorePlatform(profile);
  const platformMeta = getPlatform(platform);
  const connectionStatus = resolveConnectionStatus(profile);
  const connected = isStoreConnected(profile);

  return {
    ...profile,
    platform,
    platformMeta,
    storeId: getStoreId(profile),
    name: getStoreName(profile),
    region: getStoreRegion(profile),
    currency: getStoreCurrency(profile),
    accountLabel: profile?.accountInfo?.name || profile?.accountName || getStoreName(profile),
    connectionStatus,
    connectionStatusLabel: profile?.connectionStatusLabel || connectionStatusLabel(connectionStatus),
    isConnected: connected,
    isViewable: isStoreViewable(profile),
    isActive: connected && profile?.isActive !== false,
    viewMode: connected ? 'live' : 'cached',
  };
}

export function partitionStoresByConnection(stores = []) {
  const normalized = normalizeStoreList(stores);
  const connected = normalized.filter((s) => s.isConnected);
  const disconnected = normalized.filter((s) => !s.isConnected);
  disconnected.sort((a, b) => {
    const ta = a.disconnectedAt ? new Date(a.disconnectedAt).getTime() : 0;
    const tb = b.disconnectedAt ? new Date(b.disconnectedAt).getTime() : 0;
    return tb - ta;
  });
  return { connected, disconnected };
}

export function normalizeStoreList(profiles = []) {
  return (profiles || []).map(normalizeStoreProfile).filter(Boolean);
}

export function groupStoresByPlatform(profiles = []) {
  const groups = {};
  normalizeStoreList(profiles).forEach((store) => {
    const key = store.platform || 'amazon';
    if (!groups[key]) groups[key] = [];
    groups[key].push(store);
  });
  return groups;
}

/** Strip trailing marketplace suffixes so "Acme (IN)" and "Acme (US)" group together. */
export function getStoreBaseName(profile) {
  const name = getStoreName(profile);
  return name
    .replace(/\s*[\(\[\|]\s*(IN|US|UK|DE|FR|IT|ES|CA|AU|AE|SA|JP|MX|BR|NL|SE|PL|TR|SG|BE|EG|India|United States|United Kingdom)\s*[\)\]]?\s*$/i, '')
    .replace(/\s*-\s*(India|United States|UK|Germany|France|Italy|Spain|Canada|Australia|UAE|Saudi Arabia)\s*$/i, '')
    .trim();
}

export function groupStoresByName(profiles = []) {
  const normalized = normalizeStoreList(profiles);
  const groups = new Map();

  normalized.forEach((store) => {
    const key = getStoreBaseName(store).toLowerCase();
    if (!groups.has(key)) {
      groups.set(key, {
        id: key,
        name: getStoreBaseName(store),
        stores: [],
      });
    }
    groups.get(key).stores.push(store);
  });

  return Array.from(groups.values())
    .map((group) => {
      const regions = [...new Set(group.stores.map((s) => s.region).filter(Boolean))];
      const sorted = [...group.stores].sort((a, b) => {
        if (a.isConnected !== b.isConnected) return a.isConnected ? -1 : 1;
        return String(a.region).localeCompare(String(b.region));
      });
      return {
        ...group,
        stores: sorted,
        hasMultipleRegions: regions.length > 1,
        primaryStore: sorted.find((s) => s.isConnected) || sorted[0],
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function filterStoreGroups(groups = [], { query = '', platform = 'all' } = {}) {
  const q = query.trim().toLowerCase();
  return groups
    .map((group) => {
      const stores = filterStores(group.stores, { query: '', platform });
      if (!stores.length) return null;
      if (!q) return { ...group, stores };
      const matchesGroup =
        group.name.toLowerCase().includes(q) ||
        stores.some(
          (s) =>
            s.storeId.includes(q) ||
            s.region.toLowerCase().includes(q) ||
            s.accountLabel.toLowerCase().includes(q)
        );
      if (!matchesGroup) return null;
      const filteredStores = q
        ? stores.filter(
            (s) =>
              group.name.toLowerCase().includes(q) ||
              s.storeId.includes(q) ||
              s.region.toLowerCase().includes(q) ||
              s.name.toLowerCase().includes(q) ||
              s.accountLabel.toLowerCase().includes(q)
          )
        : stores;
      return { ...group, stores: filteredStores.length ? filteredStores : stores };
    })
    .filter(Boolean);
}

export const STORE_REGION_PICK_KEY = 'sellerclubStoreRegionPick';

export function readStoreRegionPick() {
  try {
    const raw = localStorage.getItem(STORE_REGION_PICK_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function writeStoreRegionPick(map) {
  try {
    localStorage.setItem(STORE_REGION_PICK_KEY, JSON.stringify(map || {}));
  } catch {
    /* ignore quota */
  }
}

export function findStoreGroupForProfile(profiles, profileId) {
  if (!profileId) return null;
  return groupStoresByName(profiles).find((g) =>
    g.stores.some((s) => String(s.profileId) === String(profileId))
  ) || null;
}

/** Pick which Amazon profile (marketplace) to use for a store group. */
export function resolveProfileForStoreGroup(group, { selectedProfile, regionPick = {} } = {}) {
  if (!group?.stores?.length) return null;
  const pickedId = regionPick[group.id];
  if (pickedId) {
    const picked = group.stores.find((s) => String(s.profileId) === String(pickedId));
    if (picked) return picked;
  }
  if (
    selectedProfile
    && group.stores.some((s) => String(s.profileId) === String(selectedProfile.profileId))
  ) {
    return selectedProfile;
  }
  return group.stores.find((s) => s.isConnected) || group.stores[0];
}

export function filterStores(profiles, { query = '', platform = 'all' } = {}) {
  const normalized = normalizeStoreList(profiles);
  const q = query.trim().toLowerCase();

  return normalized.filter((store) => {
    if (platform !== 'all' && store.platform !== platform) return false;
    if (!q) return true;
    return (
      store.name.toLowerCase().includes(q) ||
      store.storeId.includes(q) ||
      store.region.toLowerCase().includes(q) ||
      store.currency.toLowerCase().includes(q) ||
      store.accountLabel.toLowerCase().includes(q) ||
      store.platformMeta.name.toLowerCase().includes(q)
    );
  });
}
